/*
  Build My Look (spec chapter 28; stage א5), in the drawer from
  snippets/build-look-drawer.liquid. The AI server component (/look) builds a
  look around one anchor item in three directions: Understated first, with a
  skeleton while it loads. The anchor stays at the top; switching directions
  swaps the three items, and a direction already loaded is never sent again
  (kept in DemoStore, per anchor). "Replace" asks for another item for the same
  place; the X removes an item and the total follows. An anchor from her
  wardrobe is "already yours" and stays out of the bag and the total. Fewer
  items than three: what there is, with "these are the items that fit now".
  Opened by [data-build-look="<handle>"], with [data-build-color] and
  [data-build-owned], or in the color chosen on the product page. A product
  page opened with #build-my-look opens it at once.
*/
(() => {
  const store = () => window.DemoStore;
  const ai = () => window.AmorAI;
  const unique = (list) => [...new Set(list.filter(Boolean))];

  class AmorBuildLook extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('amor-drawer');
      this.anchorList = this.querySelector('[data-build-anchor]');
      this.list = this.querySelector('[data-build-items]');
      try {
        this.strings = JSON.parse(this.querySelector('[data-build-strings]')?.textContent || '{}');
      } catch {
        this.strings = {};
      }
      this.renderToken = 0;
      this.pending = {};
      this.items = [];
      this.addEventListener('click', (event) => this.onClick(event));
    }

    onClick(event) {
      const direction = event.target.closest('[data-build-direction]');
      if (direction) {
        this.select(direction.dataset.buildDirection);
        return;
      }
      const row = event.target.closest('[data-build-index]');
      if (row && event.target.closest('[data-build-replace]')) {
        this.replace(Number(row.dataset.buildIndex));
        return;
      }
      if (row && event.target.closest('[data-build-remove]')) {
        this.remove(Number(row.dataset.buildIndex));
        return;
      }
      if (event.target.closest('[data-build-retry]')) {
        this.retry?.();
        return;
      }
      const add = event.target.closest('[data-build-add]');
      if (add && this.addable?.length) ai().openSizeSheet(this.addable, add);
    }

    async open(entry, trigger) {
      const key = `${entry.handle}:${entry.color || ''}${entry.owned ? ':owned' : ''}`;
      if (key !== this.key) {
        this.key = key;
        this.entry = { handle: entry.handle, color: entry.color || null, owned: entry.owned === true };
        this.state = store()?.builtLook(key) || { directions: {}, shown: [] };
        this.anchor = null;
        this.direction = null;
        this.items = [];
        this.anchorList.replaceChildren();
        this.list.replaceChildren();
      }
      this.drawer.open(trigger);
      if (!this.anchor) {
        this.showLoading();
        // The anchor loads even when it is sold out or sold only in the boutique.
        const anchor = await ai().loadItem({ ...this.entry, owned: true });
        if (!anchor || this.key !== key) {
          if (!anchor) this.showError('anchor');
          return;
        }
        this.anchor = { ...anchor, owned: this.entry.owned };
        this.anchorList.replaceChildren(this.row(this.anchor, null));
      }
      this.select(this.direction || 'understated');
    }

    save() {
      store()?.setBuiltLook(this.key, this.state);
    }

    /** The anchor as /look reads it; vendor, title, type and world for an anchor outside the server's catalog. */
    anchorPayload() {
      const anchor = this.anchor;
      return {
        handle: anchor.handle,
        color: anchor.color,
        owned: this.entry.owned,
        vendor: anchor.vendor,
        title: anchor.title,
        type: anchor.type,
        world: anchor.world,
      };
    }

    select(direction) {
      if (!this.anchor) return;
      this.direction = direction;
      this.querySelectorAll('[data-build-direction]').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.buildDirection === direction))
      );
      if (this.state.directions[direction]) this.render();
      else this.request(direction);
    }

    /** Is this still the anchor and direction on screen? An answer that arrives late is kept, but not drawn. */
    showing(key, direction) {
      return this.key === key && this.direction === direction;
    }

    async request(direction) {
      this.retry = () => this.request(direction);
      this.showLoading();
      const { key, state } = this;
      const pendingKey = `${key}|${direction}`;
      if (this.pending[pendingKey]) return;
      this.pending[pendingKey] = true;
      let result;
      try {
        result = await ai().look({ anchor: this.anchorPayload(), direction, shown: state.shown });
      } catch (error) {
        delete this.pending[pendingKey];
        if (this.showing(key, direction)) this.showError(error.code);
        return;
      }
      delete this.pending[pendingKey];
      const items = (result.items || []).map(({ handle, color, reason, owned }) => ({
        handle,
        color: color || null,
        reason: reason || '',
        owned: owned === true,
      }));
      state.directions[direction] = { status: result.status, items };
      state.shown = unique([...state.shown, ...items.map((item) => item.handle)]);
      store()?.setBuiltLook(key, state);
      if (this.showing(key, direction)) this.render();
    }

    /** "Replace": one other item for the same place in the look. */
    async replace(index) {
      const { key, state, direction } = this;
      const saved = state.directions[direction];
      const entry = saved?.items[index];
      if (!entry || this.busy) return;
      this.busy = true;
      this.retry = () => this.replace(index);
      const row = this.list.querySelector(`[data-build-index="${index}"]`);
      row?.classList.add('is-busy');
      row?.querySelector('[data-build-item-status]')?.removeAttribute('hidden');
      row?.querySelector('[data-build-replace]')?.setAttribute('hidden', '');
      let result;
      try {
        result = await ai().look({
          anchor: this.anchorPayload(),
          direction,
          shown: state.shown,
          replace: entry.handle,
          current: saved.items.map((item) => item.handle),
        });
      } catch (error) {
        this.busy = false;
        if (!this.showing(key, direction)) return;
        await this.render();
        this.showError(error.code, true);
        return;
      }
      this.busy = false;
      const next = (result.items || [])[0];
      if (next) {
        saved.items[index] = { handle: next.handle, color: next.color || null, reason: next.reason || '', owned: next.owned === true };
        state.shown = unique([...state.shown, next.handle]);
      } else {
        // Nothing else fits that place: the item stays, and the line says so.
        saved.status = 'partial';
      }
      store()?.setBuiltLook(key, state);
      if (this.showing(key, direction)) this.render();
    }

    remove(index) {
      const saved = this.state.directions[this.direction];
      if (!saved || this.busy) return;
      saved.items.splice(index, 1);
      this.save();
      this.render();
    }

    /* --- Drawing ------------------------------------------------------------- */

    showLoading() {
      this.list.replaceChildren();
      this.querySelector('[data-build-loading]').hidden = false;
      this.querySelector('[data-build-partial]').hidden = true;
      this.querySelector('[data-build-error]').hidden = true;
      this.querySelector('[data-build-footer]').hidden = true;
    }

    /** "Something went wrong. Try again", with the button where trying again makes sense. */
    showError(code, keepItems) {
      if (!keepItems) this.list.replaceChildren();
      this.querySelector('[data-build-loading]').hidden = true;
      const box = this.querySelector('[data-build-error]');
      box.hidden = false;
      // Over her own limit: wait a few minutes, with Women and Men and no "try again" (stage א7).
      const limited = code === 'rate_limited';
      const text = box.querySelector('[data-build-error-text]');
      text.textContent = limited ? text.dataset.rateLimited : text.dataset.error;
      box.querySelector('[data-build-retry]').hidden = !ai().retryable(code);
      // Women and Men also under the site's daily limit (site_limit), like the answer card (full pass, stage א7).
      box.querySelector('[data-build-worlds]').hidden = !['rate_limited', 'site_limit'].includes(code);
      if (!keepItems) this.querySelector('[data-build-footer]').hidden = true;
    }

    async render() {
      const direction = this.direction;
      const token = ++this.renderToken;
      const saved = this.state.directions[direction];
      if (!saved) return;
      const loaded = await Promise.all(saved.items.map((entry) => ai().loadItem(entry)));
      if (token !== this.renderToken || this.direction !== direction) return;
      this.items = loaded.map((item, index) => item && { ...item, index }).filter(Boolean);
      this.list.replaceChildren(...this.items.map((item) => this.row(item, item.index)));
      this.querySelector('[data-build-loading]').hidden = true;
      this.querySelector('[data-build-error]').hidden = true;
      // Fewer items than asked for (the server's "partial", or sold out since): not after she removed one.
      this.querySelector('[data-build-partial]').hidden = !(saved.status === 'partial' || this.items.length < saved.items.length);
      this.updateFooter();
    }

    row(item, index) {
      const template = this.querySelector('template[data-build-row]');
      const node = document.importNode(template.content, true).firstElementChild;
      const isAnchor = index === null;
      if (!isAnchor) node.dataset.buildIndex = index;
      const image = node.querySelector('[data-build-image]');
      if (item.image) image.src = item.image.replace(/([?&])width=\d+/, '$1width=240');
      node.querySelectorAll('[data-build-link]').forEach((link) => (link.href = item.url));
      node.querySelector('[data-build-vendor]').textContent = item.vendor;
      node.querySelector('[data-build-title]').textContent = item.title;
      const counts = !item.owned && item.available;
      node.querySelector('[data-build-meta]').textContent = [item.color, counts ? ai().formatMoney(item.price, this.dataset.priceFormat) : null]
        .filter(Boolean)
        .join(' · ');
      const tag = node.querySelector('[data-build-tag]');
      if (isAnchor || item.owned) {
        tag.hidden = false;
        tag.textContent = item.owned ? this.strings.owned : this.strings.yours;
      }
      const why = node.querySelector('[data-build-why]');
      if (item.reason) {
        why.hidden = false;
        why.textContent = item.reason;
      }
      if (!isAnchor) {
        node.querySelector('[data-build-replace]').hidden = false;
        const remove = node.querySelector('[data-build-remove]');
        remove.hidden = false;
        remove.setAttribute('aria-label', remove.dataset.label.replace('__TITLE__', item.title));
      }
      return node;
    }

    /** The total and the buttons: the anchor (unless it is hers already) and the items she does not own. */
    updateFooter() {
      const anchor = this.anchor && !this.anchor.owned && this.anchor.available ? [this.anchor] : [];
      this.addable = [...anchor, ...this.items.filter((item) => !item.owned && item.available)];
      const total = this.addable.reduce((sum, item) => sum + item.price, 0);
      this.querySelector('[data-build-total]').textContent = ai().formatMoney(total, this.dataset.priceFormat);
      this.querySelector('[data-build-fitting]').href = ai().fittingUrl(this.addable);
      this.querySelector('[data-build-add]').disabled = !this.addable.length;
      this.querySelector('[data-build-footer]').hidden = !this.items.length;
    }
  }

  customElements.define('amor-build-look', AmorBuildLook);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-build-look]');
    const element = document.querySelector('amor-build-look');
    if (!trigger || !element) return;
    event.preventDefault();
    const form = trigger.closest('amor-product-form');
    element.open(
      {
        handle: trigger.dataset.buildLook,
        color: trigger.dataset.buildColor || form?.color || null,
        owned: trigger.hasAttribute('data-build-owned'),
      },
      trigger
    );
  });

  // A link to a product page with #build-my-look opens it at once.
  const openFromHash = () => {
    if (window.location.hash !== '#build-my-look') return;
    document.querySelector('amor-product-form [data-build-look]')?.click();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', openFromHash);
  else openFromHash();
})();
